// Growth › Lead finder › Review queue: /growth/finder/queue
// ?q=&status=&source=&has_email=&sort=&page=
//
// What the finder suggests, for the owner to approve. Selection is per page and
// drives Add to leads and Reject. The server hides anything on the do-not-contact
// list, so nothing here ever shows one.

import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '@/api'
import { CANDIDATE_STATUSES, FINDER_ADAPTERS, type Candidate, type CandidateSort, type CandidateStatus, type FinderAdapter } from '@/api/growthInsights'
import {
  Badge,
  Button,
  Card,
  ChevronRightIcon,
  ConfirmDialog,
  DataTable,
  EmailText,
  EmptyState,
  ErrorState,
  Pagination,
  SearchInput,
  SelectInput,
  SkeletonRows,
  useToast,
  type Column,
} from '@/components/ui'
import { VERIFY_RUN_WORDS } from '@/lib/growth'
import { CANDIDATE_SORT_OPTIONS, CANDIDATE_STATUS_LABELS, candidatePlace, SOURCE_LABELS } from '@/lib/growthInsights'
import { useDocumentTitle, useIsDesktop } from '@/lib/hooks'
import { invalidateQueries, useQuery } from '@/lib/query'
import { useActivityRun, useFinderStatus, usePoll } from '@/lib/useFinder'
import { ActivityRunBanner } from './activityShared'
import { CandidateStatusBadge, FinderFrame } from './finderShared'
import { CurrentPlatform } from './verifyShared'

const PAGE_SIZE = 25

const SOURCE_FILTERS = [{ value: '', label: 'Any source' }, ...FINDER_ADAPTERS.map((a) => ({ value: a, label: SOURCE_LABELS[a] }))]
const EMAIL_FILTERS = [
  { value: '', label: 'Email or not' },
  { value: 'true', label: 'Has email' },
  { value: 'false', label: 'No email' },
]

function readParams(p: URLSearchParams) {
  const status = p.get('status')
  const source = p.get('source')
  const sort = p.get('sort')
  const hasEmail = p.get('has_email')
  const page = Number(p.get('page'))
  return {
    q: p.get('q') ?? '',
    // The queue opens on what's waiting; `all` shows everything.
    status: status === 'all' ? undefined : (CANDIDATE_STATUSES as readonly string[]).includes(status ?? '') ? (status as CandidateStatus) : ('new' as CandidateStatus),
    source: (FINDER_ADAPTERS as readonly string[]).includes(source ?? '') ? (source as FinderAdapter) : undefined,
    has_email: hasEmail === 'true' ? true : hasEmail === 'false' ? false : undefined,
    sort: CANDIDATE_SORT_OPTIONS.some((s) => s.value === sort) ? (sort as CandidateSort) : ('-priority' as CandidateSort),
    page: Number.isInteger(page) && page > 0 ? page : 1,
  }
}

function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (e: ChangeEvent<HTMLInputElement>) => void }) {
  return (
    <label className="-m-3.5 inline-flex cursor-pointer items-center justify-center p-3.5">
      <input type="checkbox" aria-label={label} checked={checked} onChange={onChange} className="h-4 w-4 accent-accent-solid" />
    </label>
  )
}

function CandidateName({ candidate: c }: { candidate: Candidate }) {
  return (
    <span className="grid min-w-0 gap-0.5">
      <span className="break-words font-semibold">{c.name}</span>
      <span className="truncate text-xs font-medium text-muted">{candidatePlace(c) ?? c.source_url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</span>
    </span>
  )
}

function Contact({ candidate: c }: { candidate: Candidate }) {
  if (c.email) return <span className="font-mono text-xs [overflow-wrap:anywhere]"><EmailText email={c.email} /></span>
  if (c.phone) return <span className="font-mono text-xs">{c.phone}</span>
  return <span className="text-subtle">—</span>
}

function CandidateCard({ candidate: c, checked, onToggle }: { candidate: Candidate; checked: boolean; onToggle: () => void }) {
  return (
    <div className="flex items-start gap-3">
      <div className="pt-0.5">
        <Check label={`Select ${c.name}`} checked={checked} onChange={onToggle} />
      </div>
      <Link to={`/growth/finder/candidates/${c.id}`} className="flex min-w-0 flex-1 items-center gap-3">
        <div className="grid min-w-0 flex-1 gap-1.5">
          <CandidateName candidate={c} />
          <div className="flex flex-wrap items-center gap-1.5">
            <CandidateStatusBadge status={c.status} />
            {c.linked_platform ? <Badge tone="outline">{c.linked_platform.name}</Badge> : null}
            <Badge tone="outline">{SOURCE_LABELS[c.source] ?? c.source}</Badge>
          </div>
          {c.email ? <span className="truncate font-mono text-xs text-muted">{c.email}</span> : null}
        </div>
        <ChevronRightIcon size={16} className="shrink-0 text-subtle" />
      </Link>
    </div>
  )
}

function BulkBar({ ids, onDone, verifyRun }: { ids: number[]; onDone: () => void; verifyRun: ReturnType<typeof useActivityRun> }) {
  const toast = useToast()
  const [confirming, setConfirming] = useState<'add' | 'reject' | 'verify' | null>(null)
  const noun = `${ids.length} ${ids.length === 1 ? 'candidate' : 'candidates'}`

  const run = async (action: 'add' | 'reject') => {
    const r = await api.growth.finder.bulk(ids, action)
    invalidateQueries('growth')
    if (action === 'reject') toast.success(`${r.rejected.length} rejected.`)
    else {
      const unchecked = r.skipped.filter((s) => s.reason === 'not_verified' || s.reason === 'failed').length
      const other = r.skipped.length - unchecked
      const notes = [r.duplicates.length ? `${r.duplicates.length} already leads` : '', unchecked ? `${unchecked} not verified, so left out` : '', other ? `${other} skipped` : ''].filter(Boolean).join(', ')
      toast.success(`${r.added.length} added to leads${notes ? `. ${notes}.` : '.'}`)
    }
    onDone()
  }

  return (
    <div role="region" aria-label="Bulk actions" className="flex flex-wrap items-center gap-2 rounded-lg border border-line-strong bg-raised px-3 py-2">
      <span className="text-[13px] font-semibold text-ink">{ids.length} selected</span>
      <Button size="sm" variant="primary" onClick={() => setConfirming('add')}>
        Add to leads
      </Button>
      <Button size="sm" disabled={verifyRun.busy} onClick={() => setConfirming('verify')}>
        Verify
      </Button>
      <Button size="sm" onClick={() => setConfirming('reject')}>
        Reject
      </Button>
      <Button size="sm" variant="ghost" onClick={onDone}>
        Clear
      </Button>
      <ConfirmDialog
        open={confirming === 'add'}
        onClose={() => setConfirming(null)}
        title={`Add ${noun} to leads?`}
        description="Each becomes a lead with the priority shown. One that isn't verified, or failed, stays here. Nothing is sent to anyone."
        confirmLabel={`Add ${noun}`}
        tone="primary"
        onConfirm={() => run('add')}
      />
      <ConfirmDialog
        open={confirming === 'verify'}
        onClose={() => setConfirming(null)}
        title={`Verify ${noun}?`}
        description="It reads where each league lives today, in the background, and uses today's page budget."
        confirmLabel={`Verify ${noun}`}
        tone="primary"
        onConfirm={async () => {
          const started = await api.growth.finder.verify(ids)
          verifyRun.started(started.id)
          onDone()
        }}
      />
      <ConfirmDialog
        open={confirming === 'reject'}
        onClose={() => setConfirming(null)}
        title={`Reject ${noun}?`}
        description="A rejected candidate isn't suggested again."
        confirmLabel={`Reject ${noun}`}
        onConfirm={() => run('reject')}
      />
    </div>
  )
}

export default function FinderQueuePage() {
  useDocumentTitle('Lead finder')
  const isDesktop = useIsDesktop()
  const [searchParams, setSearchParams] = useSearchParams()
  const { q, status, source, has_email, sort, page } = readParams(searchParams)

  const update = (patch: Record<string, string | number | boolean | undefined>) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined || v === '' || (k === 'sort' && v === '-priority') || (k === 'page' && v === 1) || (k === 'status' && v === 'new')) next.delete(k)
          else next.set(k, String(v))
        }
        if (!('page' in patch)) next.delete('page')
        return next
      },
      { replace: true },
    )

  const verifyRun = useActivityRun('verify')
  const params = useMemo(() => ({ q, status, source, has_email, sort, page, page_size: PAGE_SIZE }), [q, status, source, has_email, sort, page])
  const { data, error, status: loadStatus, isFetching, refetch } = useQuery(['growth', 'candidates', params], (signal) => api.growth.finder.candidates(params, signal), { keepPrevious: true })

  // What a run finds arrives while the queue is open. This asks for the status
  // itself, because a query's copy isn't shared: it has to see the run end to stop.
  const { data: finder, refetch: refetchFinder } = useFinderStatus()
  const following = !!finder?.active_run_id
  const follow = useCallback(() => {
    refetch()
    refetchFinder()
  }, [refetch, refetchFinder])
  usePoll(following, follow)
  // One last read when it ends: its last candidates arrive with it.
  const wasFollowing = useRef(false)
  useEffect(() => {
    if (wasFollowing.current && !following) refetch()
    wasFollowing.current = following
  }, [following, refetch])

  const [selected, setSelected] = useState<Set<number>>(new Set())
  const paramsKey = JSON.stringify(params)
  useEffect(() => setSelected(new Set()), [paramsKey])

  const rows = data?.candidates ?? []
  const counts = data?.status_counts
  const filtered = !!q || !!source || has_email !== undefined

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (!next.delete(id)) next.add(id)
      return next
    })
  const allSelected = rows.length > 0 && rows.every((c) => selected.has(c.id))

  const columns: Column<Candidate>[] = [
    {
      id: 'select',
      header: <Check label="Select all on this page" checked={allSelected} onChange={() => setSelected(allSelected ? new Set() : new Set(rows.map((c) => c.id)))} />,
      cell: (c) => <Check label={`Select ${c.name}`} checked={selected.has(c.id)} onChange={() => toggle(c.id)} />,
      className: 'w-10',
      hideOnMobile: true,
    },
    // The columns for deciding; verification, activity, bucket and confidence are on the candidate's page (owner, 2026-11-03).
    { id: 'name', header: 'League', primary: true, cell: (c) => <CandidateName candidate={c} />, className: 'min-w-[14rem] max-w-[22rem]' },
    { id: 'platform', header: 'Uses now', cell: (c) => <CurrentPlatform facts={c} linked={c.linked_platform} text={c.current_platform} /> },
    { id: 'contact', header: 'Public contact', cell: (c) => <Contact candidate={c} />, className: 'max-w-[16rem]' },
    { id: 'source', header: 'Found on', cell: (c) => <span className="text-muted">{SOURCE_LABELS[c.source] ?? c.source}</span> },
    { id: 'status', header: 'Status', cell: (c) => <CandidateStatusBadge status={c.status} /> },
  ]

  return (
    <FinderFrame>
      <div className="grid gap-3">
        {/* One toolbar (owner, 2026-11-03), as on Leads. */}
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
          <SearchInput label="Search candidates" placeholder="Search name, email, city or website" value={q} onChange={(v) => update({ q: v })} className="lg:max-w-sm lg:flex-1" />
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center lg:ml-auto">
            <SelectInput
              aria-label="Filter by status"
              value={status ?? 'all'}
              options={(['new', 'added', 'duplicate', 'rejected', 'all'] as const).map((s) => ({ value: s, label: `${s === 'all' ? 'Any status' : CANDIDATE_STATUS_LABELS[s]}${counts ? ` (${counts[s]})` : ''}` }))}
              onChange={(e) => update({ status: e.target.value })}
              className="h-8 sm:w-40"
            />
            <SelectInput aria-label="Filter by source" value={source ?? ''} options={SOURCE_FILTERS} onChange={(e) => update({ source: e.target.value })} className="h-8 sm:w-40" />
            <SelectInput aria-label="Filter by email" value={has_email === undefined ? '' : String(has_email)} options={EMAIL_FILTERS} onChange={(e) => update({ has_email: e.target.value })} className="h-8 sm:w-36" />
            <SelectInput aria-label="Sort candidates" value={sort} options={CANDIDATE_SORT_OPTIONS} onChange={(e) => update({ sort: e.target.value })} className="h-8 sm:w-44" />
          </div>
        </div>
      </div>

      <ActivityRunBanner follow={verifyRun} words={VERIFY_RUN_WORDS} />
      {selected.size > 0 ? <BulkBar ids={[...selected]} onDone={() => setSelected(new Set())} verifyRun={verifyRun} /> : null}

      <Card flush className="overflow-hidden">
        {loadStatus === 'loading' ? (
          <SkeletonRows rows={6} label="Loading candidates" />
        ) : loadStatus === 'error' && !data ? (
          <ErrorState error={error} title="Couldn't load the queue" onRetry={refetch} />
        ) : rows.length === 0 ? (
          data && data.total > 0 ? (
            <EmptyState
              title="No results on this page"
              description={`Page ${page} is past the end.`}
              action={
                <Button size="sm" onClick={() => update({ page: 1 })}>
                  Go to page 1
                </Button>
              }
            />
          ) : filtered ? (
            <EmptyState title="No candidates match" description="Try another search or filter." />
          ) : (
            <EmptyState title="Nothing to review" description="Start a run to find leagues. What it finds waits here for you." />
          )
        ) : (
          <>
            <DataTable
              caption="Candidates"
              rows={rows}
              columns={columns}
              rowKey={(c) => c.id}
              rowHref={isDesktop ? (c) => `/growth/finder/candidates/${c.id}` : undefined}
              renderCard={(c) => <CandidateCard candidate={c} checked={selected.has(c.id)} onToggle={() => toggle(c.id)} />}
              busy={isFetching && loadStatus === 'success'}
            />
            <div className="border-t border-line px-4 py-3">
              <Pagination page={data?.page ?? page} pageSize={data?.page_size ?? PAGE_SIZE} total={data?.total ?? 0} noun="candidates" onPageChange={(p) => update({ page: p })} />
            </div>
          </>
        )}
      </Card>
    </FinderFrame>
  )
}
